import React, {useEffect, useState, useRef, useCallback} from "react";
import { useLocation, useNavigate, useParams} from "react-router-dom";
import { useGetTocsAndRelatedSchoolsByBookId, fetchPersonOrders } from './useRedbook';
import RedbookData from "./RedbookData";
import ReactModal from "react-modal";
import {selectedPerson, selectedSchool} from "../../../recoils/recoil";
import {useRecoilValue, useSetRecoilState} from "recoil";
import EmptyPage from "../../../components/EmptyPage";

/**
 * 아카이브 > 조선 총독부 직원록 중 학교 및 교원정보 > 상세 > 리스트
 */
const RedbookId = () => {
    const selectedPersonList = useRecoilValue(selectedPerson);
    const setSelectedPersonList = useSetRecoilState(selectedPerson);
    const selectedSchoolList = useRecoilValue(selectedSchool);
    const setSelectedSchoolList = useSetRecoilState(selectedSchool);

    const navigate = useNavigate();
    const { book_id } = useParams();  // URL에서 book_id 가져오기
    const [bookDetails, setBookDetails] = useState(null);
    const [loading, setLoading] = useState(true);  // 로딩 상태 관리
    const location = useLocation();

    const [page, setPage] = useState(1);
    const [displayedData, setDisplayedData] = useState([]);
    const [selectedTocData, setSelectedTocData] = useState(null);
    const [personOrders, setPersonOrders] = useState([]);
    const [personLoading, setPersonLoading] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const observerRef = useRef();
    const loadingRef = useRef();

    // 모바일 툴팁 관련
    const [isTooltipVisible, setTooltipVisible] = useState(false);
    const toolTipRef = useRef(null);

    const ITEMS_PER_PAGE = 10;
    // book_id로 관련 데이터 가져오기 (API 호출)
    const datas = useGetTocsAndRelatedSchoolsByBookId(book_id);

    useEffect(() => {
        setLoading(true);

        // RedbookData에서 책 기본 정보 찾기
        const foundBook = RedbookData.find(book => book.book_id === parseInt(book_id));

        if (foundBook) {
            setBookDetails(foundBook);
        }

        setLoading(false);

        // 모바일 툴팁 외부 영역 클릭 시 툴팁 닫기
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [book_id]);

    const handleClickOutside = (e) => {
        if (toolTipRef.current && !toolTipRef.current.contains(e.target)) {
            setTooltipVisible(false);
        }
    };

    // 무한 스크롤 관찰자 설정
    const lastElementRef = useCallback(node => {
        if (loading) return;
        if (observerRef.current) observerRef.current.disconnect();

        observerRef.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && displayedData.length < datas.length) {
                setPage(prevPage => prevPage + 1);
            }
        });

        if (node) observerRef.current.observe(node);
    }, [loading, datas.length, displayedData.length]);


    // 페이지네이션 데이터 처리
    useEffect(() => {
        const endIndex = page * ITEMS_PER_PAGE;
        setDisplayedData(datas.slice(0, endIndex));
        setLoading(false);
    }, [page, datas]);
    
    // 인물, 학교 상세 검색에서 오면 팝업 띄우기
    useEffect( () => {

        const order = location.state ? location.state.order : null;

        if (order !== null) {
            // 데이터 가져오기
            handleTocClick(order);
        }

    }, [book_id, location.state]);

    const handleTocClick = async (toc) => {
        try {
            if (toc.n_psnorders === 0) return;

            setPersonLoading(true);
            setSelectedTocData(toc);
            setIsPopupOpen(true);

            const personData = await fetchPersonOrders(toc.toc_id);

            if (personData && Array.isArray(personData)) {
                setPersonOrders(personData);
            } else {
                setPersonOrders([]);
            }
        } catch (error) {
            setPersonOrders([]);
        } finally {
            setPersonLoading(false);
        }
    };

    const handlePersonClick = (order) => {
        // 중복된 person_id가 이미 존재하는 경우 추가하지 않음
        const exists = selectedPersonList.some(person => person.id === order.person_id);

        const updatedList = exists
            ? [...selectedPersonList] // 기존 순서 유지
            : [...selectedPersonList, { id: order.person_id, name: order.personname, nameKo: order.personname }];

        setSelectedPersonList(updatedList);

        const idList = updatedList.map(person => person.id);

        // 배열에 넣어서 personId 보내기
        navigate(`/person/${idList.join(',')}`);
    };

    const handleSchoolClick = (order) => {
        const exists = selectedSchoolList.some(school => school.id === order.school_id);

        const updatedList = exists
            ? [...selectedSchoolList] // 기존 순서 유지
            : [...selectedSchoolList, { id: order.school_id, name: order.primaryname, nameKo: order.primaryname_ko }];

        setSelectedSchoolList(updatedList);

        const idList = updatedList.map(school => school.id);

        // 배열에 넣어서 personId 보내기
        navigate(`/school/${idList.join(',')}`);
    };

    const closePopup = () => {
        setIsPopupOpen(false);
        setSelectedTocData(null);
        setPersonOrders([]);
    };

    // book_id가 없으면 바로 로딩 메시지 반환
    if (!book_id || loading || !bookDetails) {

        return <EmptyPage/>
    }

    return (
        <div>
            <div id="container">
                <main id="contents">
                    <div className="sub-content">
                        <div className="archive-wrap">
                            <div className="cont-top">
                                <div className="w-set">
                                    <div className="title-wrap">
                                        <h2 className="title batang">조선총독부 직원록 중 <br className="hide-pc"/>학교 및 교원정보</h2>
                                        <p className="text">조선총독부가 조선 전체 직원에 대한 직원 명단을 정리하여 <br/>책으로 출간하였답니다.</p>
                                    </div>
                                </div>
                            </div>
                            <div className="cont-btm">
                                <div className="archive-text-box">
                                    <div className="w-set">
                                        <div className="text-wrap">
                                            <p className="list-name">
                                                <span>{bookDetails.book_title}</span>
                                                <span>{bookDetails.book_title_ko}</span>
                                                <span>{bookDetails.publish_year}</span>
                                                <span>{bookDetails.publish_date}</span>
                                                <span>{bookDetails.publisher}</span>
                                            </p>
                                        </div>
                                        {/*<div className="archive-slides" style={{display: 'none'}}>*/}
                                        {/*    <div className="swiper">*/}
                                        {/*        <div className="swiper-wrapper">*/}
                                        {/*            <div className="swiper-slide">*/}
                                        {/*                <img src="/markup/images/dummy/redbook1.png" alt="조선총독부 직원록"/>*/}
                                        {/*            </div>*/}
                                        {/*        </div>*/}
                                        {/*    </div>*/}
                                        {/*</div>*/}
                                    </div>
                                </div>
                                <div className="list-wrap table-contents">
                                    <div className="w-set">
                                        <div className="list-btm">
                                            <div className="table-wrap">
                                                <div className="table-top">
                                                    <strong>목차</strong>
                                                    <div className="tooltip-wrap" ref={toolTipRef}>
                                                        <button type="button" className="i-tool"
                                                            onClick={() => setTooltipVisible(!isTooltipVisible)}
                                                        ></button>
                                                        <div className={`tool-box ${isTooltipVisible ? 'on' : ''}`}>학교명 클릭 시 검색페이지로 이동합니다.</div>
                                                    </div>
                                                    <div className="back-list">
                                                        <div className="back" onClick={() => navigate(-1)}>뒤로가기</div>
                                                    </div>
                                                </div>
                                                <div className="scroll-table">
                                                    {displayedData.map((toc, index) => (
                                                        <div key={toc.toc_id}
                                                             className="list-row"
                                                             ref={index === displayedData.length - 1 ? lastElementRef : null}>
                                                            <div
                                                               className={`row-link ${toc.n_psnorders === 0 ? 'disabled' : ''}`}
                                                               onClick={(e) => {
                                                                    e.preventDefault();  // 기본 동작인 URL 변경을 방지
                                                                    handleTocClick(toc);  // 원하는 동작 처리
                                                                }}
                                                                >
                                                                <div className="title bold">
                                                                    {toc.toc_title}
                                                                </div>
                                                                <div className="title-ko align-end">
                                                                    {toc.toc_title_ko}
                                                                    {/* 관련 학교 버튼들 */}
                                                                    {toc.related_schools.map((school) => (
                                                                        <button
                                                                            key={school.school_id}
                                                                            className="search-text"
                                                                            onClick={() => handleSchoolClick(school)}
                                                                        >
                                                                            {school.primaryname}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {loading && <div ref={loadingRef} className="loading">로딩중...</div>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            <ReactModal
                isOpen={isPopupOpen}
                onRequestClose={closePopup}
                contentLabel="First Modal"
                style={{
                    overlay: {
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1000,
                    },
                    content: {
                        inset:"unset",
                        padding:0,
                        border:"none",
                        borderRadius:0,
                        background: "transparent",
                        width:"auto",
                        height:"auto"
                    }
                }}
            >
            {/* Popup */}
            {isPopupOpen && selectedTocData && (
                <div className={`wbz-popup-cont is-template ${isPopupOpen ? 'on' : ''}`} id="redbookViewPopup">
                    <div className="popup-header">
                        <b className="popup-title">{selectedTocData.toc_title}</b>
                        <button type="button" className="popup-close" onClick={closePopup}>닫기</button>
                    </div>
                    <div className="popup-cont">
                        {personLoading ? (
                            <div className="loading">데이터를 불러오는 중...</div>
                        ) : (
                            <div className="view-table">
                                {personOrders.length > 0 ? (
                                    personOrders.map((person, index) => (
                                        <div key={`${person.psnorders_id}_${index}`} className="list-row">
                                            <div className="paerson-name">
                                                <button type="button" className="search-text" onClick={() => handlePersonClick(person)}>
                                                    {person.personname}
                                                </button>
                                            </div>
                                            <div className="status-text">{person.status_text}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-data">데이터가 없습니다.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            </ReactModal>
        </div>
    );
};

export default RedbookId;